// Shipper pages: dashboard (my requests + analytics) and new-request form.
import React from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Plus } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useUnreadThreads } from "@/hooks/useUnreadThreads";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { DatePicker, F, TextInput, RequestCard, formatMins, Skeletons, EmptyState } from "./shared";

export function ShipperDashboard() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const { by_request: unreadByRequest } = useUnreadThreads();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/requests/mine");
        if (alive) setRequests(data);
      } catch (err) {
        toast.error(apiErrorMessage(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    api.get("/shippers/me/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const statsById = {};
  (analytics?.requests || []).forEach((row) => {
    statsById[row.id] = row;
  });
  const t = analytics?.totals;

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-6xl mx-auto" data-testid="shipper-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Your board</span>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">
            My requests
          </h1>
        </div>
        <Link
          to="/app/new"
          data-testid="new-request-cta"
          className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold uppercase tracking-widest text-xs border-2 border-[#FF3B30] hover:border-[#D62828] py-3 px-5 transition-all"
        >
          <Plus className="w-4 h-4" /> New request
        </Link>
      </div>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 border border-white/[0.08] bg-[#0F0F10] mb-8" data-testid="shipper-analytics">
          {[
            { label: "Requests", value: t.requests, testid: "stat-requests" },
            { label: "Driver views", value: t.views, testid: "stat-views" },
            { label: "Total bids", value: t.bids, testid: "stat-bids" },
            { label: "Avg 1st bid", value: t.avg_first_bid_min != null ? formatMins(t.avg_first_bid_min).replace("in ", "") : "—", testid: "stat-avg-ttfb" },
          ].map((c, i) => (
            <div key={c.label} className={`p-5 ${i > 0 ? "border-l border-white/[0.06]" : ""} ${i > 1 ? "max-md:border-l-0 max-md:border-t max-md:border-white/[0.06]" : ""} ${i === 3 ? "max-md:border-l max-md:border-white/[0.06]" : ""}`} data-testid={c.testid}>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{c.label}</div>
              <div className="font-display text-2xl font-black mt-1">{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {t && analytics?.daily && (
        <div className="border border-white/[0.08] bg-[#0F0F10] p-5 mb-8" data-testid="shipper-trends">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
              Last 30 days — traction
            </div>
            <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest font-bold">
              <span className="inline-flex items-center gap-1.5 text-zinc-400">
                <span className="w-2 h-2 bg-zinc-500" /> Views
              </span>
              <span className="inline-flex items-center gap-1.5 text-[#FF3B30]">
                <span className="w-2 h-2 bg-[#FF3B30]" /> Bids
              </span>
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics.daily} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => d.slice(5)}
                  tick={{ fill: "#71717A", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  interval={6}
                />
                <YAxis allowDecimals={false} tick={{ fill: "#71717A", fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: "#0F0F10", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
                  labelStyle={{ color: "#A1A1AA" }}
                />
                <Area type="monotone" dataKey="views" stroke="#71717A" fill="#71717A" fillOpacity={0.15} strokeWidth={1.5} name="Views" />
                <Area type="monotone" dataKey="bids" stroke="#FF3B30" fill="#FF3B30" fillOpacity={0.18} strokeWidth={2} name="Bids" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {loading ? (
        <Skeletons />
      ) : requests.length === 0 ? (
        <EmptyState
          title="No requests yet"
          body="Post your first haul — free to list, get bids within 72 hours."
          cta={{ to: "/app/new", label: "Post a request" }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {requests.map((r) => (
            <RequestCard key={r.id} r={r} to={`/app/requests/${r.id}`} testid={`request-${r.id}`} stats={statsById[r.id]} unread={unreadByRequest[r.id]} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Shipper: New Request ----------
export function NewRequest() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "",
    vehicle_type: "boat",
    description: "",
    dimensions: "",
    weight_lbs: "",
    hitch_type: "",
    pickup_city: "",
    delivery_city: "",
    photo_url: "",
    budget_usd: "",
    haul_start: "",
    haul_end: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        weight_lbs: form.weight_lbs ? parseInt(form.weight_lbs, 10) : null,
        budget_usd: form.budget_usd ? parseInt(form.budget_usd, 10) : null,
      };
      const { data } = await api.post("/requests", payload);
      toast.success("Request posted. Drivers will start bidding.");
      nav(`/app/requests/${data.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to post request"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-3xl mx-auto" data-testid="new-request-page">
      <div className="mb-10">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ New</span>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">
          Post a haul
        </h1>
        <p className="text-sm text-zinc-500 mt-3">Free to post. Bids typically come in within 72 hours.</p>
      </div>

      <form onSubmit={onSubmit} className="space-y-5" data-testid="new-request-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Vehicle type">
            <select
              name="vehicle_type"
              value={form.vehicle_type}
              onChange={onChange}
              data-testid="input-vehicle-type"
              className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white p-4"
            >
              <option value="boat">Boat</option>
              <option value="rv">RV / Fifth-wheel</option>
              <option value="trailer">Trailer</option>
              <option value="other">Other</option>
            </select>
          </F>
          <TextInput label="Title" name="title" value={form.title} onChange={onChange} placeholder="24' Wakeboat, Boise → Coeur d'Alene" testid="input-title" required />
        </div>
        <F label="Description">
          <textarea
            name="description"
            value={form.description}
            onChange={onChange}
            placeholder="Include condition, hitch situation, launch requirements, timing flexibility, and anything a driver should know."
            data-testid="input-description"
            required
            rows={4}
            className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4"
          />
        </F>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput label="Pickup city" name="pickup_city" value={form.pickup_city} onChange={onChange} placeholder="Boise, ID" testid="input-pickup" required />
          <TextInput label="Delivery city" name="delivery_city" value={form.delivery_city} onChange={onChange} placeholder="Coeur d'Alene, ID" testid="input-delivery" required />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TextInput label="Dimensions (L x W x H)" name="dimensions" value={form.dimensions} onChange={onChange} placeholder="24' x 8.5' x 9'" testid="input-dimensions" />
          <TextInput label="Weight (lbs)" name="weight_lbs" type="number" value={form.weight_lbs} onChange={onChange} placeholder="7500" testid="input-weight" />
          <TextInput label="Hitch type" name="hitch_type" value={form.hitch_type} onChange={onChange} placeholder="Class IV, 2-5/16 ball" testid="input-hitch" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput label="Photo URL (optional)" name="photo_url" value={form.photo_url} onChange={onChange} placeholder="https://…" testid="input-photo" />
          <TextInput label="Budget (USD, optional)" name="budget_usd" type="number" value={form.budget_usd} onChange={onChange} placeholder="1200" testid="input-budget" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Haul window start (optional)">
            <DatePicker
              value={form.haul_start}
              onChange={(v) => setForm((f) => ({ ...f, haul_start: v }))}
              testid="input-haul-start"
            />
          </F>
          <F label="Haul window end (optional)">
            <DatePicker
              value={form.haul_end}
              onChange={(v) => setForm((f) => ({ ...f, haul_end: v }))}
              testid="input-haul-end"
            />
          </F>
        </div>
        <p className="text-[11px] text-zinc-600 -mt-2">
          Drivers get a heads-up if your window overlaps their blackout dates.
        </p>

        <button
          type="submit"
          disabled={submitting}
          data-testid="submit-new-request"
          className="group w-full bg-[#FF3B30] hover:bg-[#D62828] disabled:opacity-60 text-white font-bold uppercase tracking-widest border-2 border-[#FF3B30] hover:border-[#D62828] py-4 px-6 flex items-center justify-center gap-3 transition-all"
        >
          {submitting ? "Posting…" : "Post request"}
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </form>
    </div>
  );
}

